import {
  Css,
  documentScrollChromeLeft,
  documentScrollChromeWidth,
  FilterDefs,
  GridTableLayout,
  multiFilter,
  pageContentPaddingX,
  PageHeaderLayout,
  toggleFilter,
  useComputed,
  useGridTableApi,
  useGridTableLayoutState,
  useModal,
} from "@homebound/beam";
import { useCallback, useMemo } from "react";
import { ItemTemplateFilter, ItemTemplateStatus, Maybe, Stage } from "src/generated/graphqlTypes";
import { useRouteParams } from "src/hooks/useRouteParams";
import { AssignElevationModal } from "src/routes/developments/components/AssignElevationsModalButton";
import { useDevelopmentContext } from "src/routes/developments/context/DevelopmentContext";
import {
  DevelopmentScopeDevelopmentFragment,
  DevelopmentScopeTemplatesQuery,
  useDevelopmentScopeTemplatesMetadataQuery,
  useDevelopmentScopeTemplatesQuery,
} from "src/routes/developments/templates/DevelopmentScopeTemplates.generated";
import {
  createScopeTemplateColumns,
  createScopeTemplateTableRows,
  DevelopmentScopeTemplatesTableRow,
} from "src/routes/developments/templates/DevelopmentScopeTemplatesTable";
import { queryResult } from "src/utils";
import { CostMappingSummaryCard } from "./components/CostMappingSummaryCard";
import { useDevelopmentScopeTemplateMenu } from "./components/DevelopmentScopeTemplateButtonMenu";

export function DevelopmentScopeTemplatesPage() {
  const { developmentId } = useRouteParams<{ developmentId: string }>();
  const metadataQuery = useDevelopmentScopeTemplatesMetadataQuery({
    variables: { developmentId },
    fetchPolicy: "cache-and-network",
  });
  return queryResult(metadataQuery, {
    data: ({ development }) => <DevelopmentScopeTemplates development={development} />,
  });
}

type DevelopmentScopeTemplatesProp = { development: DevelopmentScopeDevelopmentFragment };
type ItemTemplateFilterWithArchive = ItemTemplateFilter & { archived: Maybe<boolean> };

function DevelopmentScopeTemplates(props: DevelopmentScopeTemplatesProp) {
  const { development } = props;
  const { costMappingProgress } = development;
  const { name } = useDevelopmentContext();
  const { openModal } = useModal();
  const filterDefs: FilterDefs<ItemTemplateFilterWithArchive> = useMemo(() => {
    const status = multiFilter({
      label: "Status",
      options: [
        { name: "Active", code: ItemTemplateStatus.Active },
        { name: "Draft", code: ItemTemplateStatus.Draft },
      ],
      getOptionLabel: ({ name }) => name,
      getOptionValue: ({ code }) => code,
    });
    const archived = toggleFilter({ label: "Show Archived" });
    return { status, archived };
  }, []);

  const layoutState = useGridTableLayoutState<ItemTemplateFilterWithArchive>({
    // ensure filter storage key is unique to this development as the filterable options change per development.
    persistedFilter: { filterDefs, storageKey: `developmentTemplateFilter_${development.id}` },
    search: "client",
  });

  const query = useDevelopmentScopeTemplatesQuery({
    variables: { filter: { development: [development.id], isLatest: true, ...mapToFilter(layoutState.filter) } },
    fetchPolicy: "cache-and-network",
  });
  const itemTemplates = useMemo(() => query.data?.itemTemplates ?? [], [query.data]);

  const { totalScheduleTemplateItemMappings, totalMappingsForDevelopment, showCostMappingProgress } =
    costMappingProgress;

  const onSave = useCallback(() => query.refetch(), [query]);

  const tableApi = useGridTableApi<DevelopmentScopeTemplatesTableRow>();
  const selectedLineItemIds = useComputed(() => tableApi.getSelectedRowIds("itemTemplate"), [tableApi]);

  const actionsMenu = useDevelopmentScopeTemplateMenu({
    developmentId: development.id,
    itemTemplates: itemTemplates.filter((it) => selectedLineItemIds.includes(it.id)),
    tableRefetch: onSave,
  });

  const columns = useMemo(
    () => createScopeTemplateColumns(development.id, onSave, itemTemplates),
    [development.id, onSave, itemTemplates],
  );

  return (
    <PageHeaderLayout
      pageHeader={{
        title: "Templates",
        documentTitleSuffix: name,
        actions: [
          {
            label: "Assign elevations",
            variant: "secondary",
            onClick: () =>
              openModal({ content: <AssignElevationModal developmentId={development.id} onSave={onSave} /> }),
          },
        ],
      }}
    >
      {showCostMappingProgress && (
        // Sticky `left` (no `top`) pins the card horizontally while the wide table scrolls under it
        <div
          css={{
            ...pageContentPaddingX,
            ...Css.mb3.df.sticky.left(documentScrollChromeLeft()).w(documentScrollChromeWidth()).$,
          }}
        >
          <CostMappingSummaryCard
            progress={Math.round((totalScheduleTemplateItemMappings / totalMappingsForDevelopment) * 100)}
            progressBarText={"complete"}
            developmentId={development.id}
            mapCount={`${totalScheduleTemplateItemMappings}/${totalMappingsForDevelopment}`}
          />
        </div>
      )}
      <GridTableLayout
        layoutState={layoutState}
        hideEditColumns
        actions={[{ kind: "menu", ...actionsMenu }]}
        tableProps={{
          api: tableApi,
          query,
          createRows,
          columns,
          style: { rowHeight: "fixed" },
          sorting: { on: "client", initial: ["planName", "ASC"] },
          emptyFallback: "There are no scope templates for this development yet.",
        }}
      />
    </PageHeaderLayout>
  );
}

function createRows(data: DevelopmentScopeTemplatesQuery | undefined) {
  return createScopeTemplateTableRows(data?.itemTemplates ?? []);
}

function mapToFilter(filter: ItemTemplateFilterWithArchive): ItemTemplateFilter {
  const { archived, status, ...others } = filter;
  const defaultStatus = [ItemTemplateStatus.Active, ItemTemplateStatus.Draft];
  const isArchived: ItemTemplateStatus[] = archived ? [ItemTemplateStatus.Archived] : [];
  let selectedStatus: ItemTemplateStatus[] = [];

  if (status) selectedStatus = [...status];

  return {
    ...others,
    stage: [Stage.Construction],
    status: [
      ...(isArchived.nonEmpty
        ? [...isArchived, ...(selectedStatus.length <= 0 ? defaultStatus : selectedStatus)]
        : selectedStatus.length > 0
          ? selectedStatus
          : defaultStatus),
    ],
  };
}
